import type { StrapiParsedComponent } from '@autobid/strapi-integration/typescript/strapi/Page'
import type { Popup } from '@autobid/strapi-integration/typescript/strapi/Popup'
import type { RestComponent } from '@autobid/strapi-integration/utils/content'
import { extractStrapiComponents } from '@autobid/strapi-integration/utils/content'
import { getError } from '@autobid/strapi-integration/utils/getError'
import { defineStore } from 'pinia'
import { ref } from 'vue'

type PopupData = Popup & {
  contentComponents: StrapiParsedComponent[]
}

export const usePopupStore = defineStore('usePopup', () => {
  const activePopups = ref<Record<string, boolean>>({})
  const cmsPopupsData = ref<Record<string, PopupData>>({})
  const cmsPopupsParams = ref<Record<string, Record<string, string>>>({})
  const { locale } = useI18n()
  const { $trpc } = useNuxtApp()
  const popupFetchLocks = ref<Record<string, Promise<void> | null>>({})

  const openPopup = (slug: string) => {
    activePopups.value[slug] = true
  }

  const closePopup = (slug: string) => {
    activePopups.value[slug] = false

    // wait till animation end
    setTimeout(() => {
      delete activePopups.value[slug]
    }, 300)
  }

  const prefetchPopup = async (slug: string, draft?: boolean) => {
    if (cmsPopupsData.value[slug])
      return

    if (popupFetchLocks.value[slug])
      return popupFetchLocks.value[slug]

    popupFetchLocks.value[slug] = (async () => {
      try {
        const data = await $trpc.cms.getPopup.query({
          slug,
          locale: locale.value,
          draft,
        })

        if (!data)
          return

        cmsPopupsData.value[slug] = {
          ...data,
          localizations: data.localizations as Popup['localizations'],
          header: data.header as Popup['header'],
          contentComponents: extractStrapiComponents(
            data.contentSections as RestComponent[],
          ),
        }
      }
      catch (error) {
        const { handled, message } = getError(error)
        if (!handled)
          push.error(message)
      }
      finally {
        delete popupFetchLocks.value[slug]
      }
    })()

    return popupFetchLocks.value[slug]
  }

  const getPopupSlug = (target: HTMLElement) => {
    const href = target.getAttribute('href')
    const isPopup = href?.includes('#popup-')

    if (!isPopup)
      return

    return href!.split('popup-')[1]
  }

  function getLink(element: EventTarget | null) {
    if (element instanceof HTMLAnchorElement) {
      return element
    }
    if (element instanceof HTMLElement) {
      return element.closest('a')
    }
    return null
  }

  const handleClickLink = async (e: Event) => {
    const target = getLink(e.target)
    if (!target)
      return
    const popupSlug = getPopupSlug(target)

    if (!popupSlug)
      return

    e.preventDefault()

    target.classList.add('animate-pulse')
    await prefetchPopup(popupSlug)
    target.classList.remove('animate-pulse')
    openPopup(popupSlug)
  }

  const handleHoverLink = async (e: Event) => {
    const target = getLink(e.target)
    if (!target)
      return
    const popupSlug = getPopupSlug(target)

    if (!popupSlug)
      return

    await prefetchPopup(popupSlug)
  }

  const initListener = () => {
    window.addEventListener('click', handleClickLink)
    window.addEventListener('mouseover', handleHoverLink)
  }

  const disableListener = () => {
    window.removeEventListener('click', handleClickLink)
    window.removeEventListener('mouseover', handleHoverLink)
  }

  return {
    activePopups,
    cmsPopupsData,
    cmsPopupsParams,
    handleClickLink,
    openPopup,
    closePopup,
    prefetchPopup,
    initListener,
    disableListener,
  }
})
